/**
 * teamView.ts — Home tabs H-4: Team, read-only (spec §5, with the rulings of
 * 2026-10-18 and 2026-10-21).
 *
 * Every figure is read off what Home already loads, nothing is estimated:
 *   · the deals list (open pipeline, stages, the per-deal R1 assessment, the
 *     owner's employee id) — re-read with excludeTest when the Home toggle is
 *     on (the server's ONE test-deal definition, never a client copy);
 *   · the quarter's /v1/sales/forecast rows — the rep roster with employee
 *     names (a rep with only wins, or only a quota, still has a row) and each
 *     rep's won in the quarter; unattributedWonPence is the won value with no
 *     employee at all, the only figure called "unassigned";
 *   · the activity history (every status) — "This week" is calls, emails and
 *     meetings COMPLETED in the London week, credited to the activity's
 *     ASSIGNEE (assignedEmployeeId); attribution counts rows with no author.
 *
 * Lead routing and Assign are NOT built (deferred until after R4) and nothing
 * here promises them. Dismissed flags are honoured through homeView's
 * dismissedFor (the H-3b-2 helper): a risk count leaves out a deal whose
 * families are all dismissed and says how many it held back.
 */
import type { SalesDeal, SalesActivity } from '../../types'
import type { SalesForecast } from '../../../../api/types'
import { plural } from '../../../../lib/plural'
import { isOpenDeal, stageDisplay, dismissedFor, CONCENTRATION_PCT, sharePct } from './homeView'
import { weekStart, FIRST_STAGE } from './myDayView'
import { londonDay } from './workingDays'

type Fmt = (pence: number, currency?: string) => string

/** The roles that always see Team (no manager role exists — §38.69). */
const TEAM_ROLES = new Set(['owner', 'admin'])

/** Team shows for owner/admin, or when two or more employees own deals
 *  (RULED 2026-10-18). The role is /v1/me's; null (not loaded, or no
 *  session) is not a role — the deals rule still applies. */
export function teamVisible(role: string | null | undefined, deals: readonly SalesDeal[]): boolean {
  if (role && TEAM_ROLES.has(role)) return true
  return new Set(deals.filter(d => d.assignedEmployeeId).map(d => d.assignedEmployeeId)).size >= 2
}

const checkOf = (d: SalesDeal, id: 'recentActivity' | 'closeDate') => d.assessment?.checks.find(c => c.id === id)
const LOGGED_TYPES = new Set(['call', 'email', 'meeting'])
const ago = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10)

/** A completed call, email or meeting and the London day it was completed. */
function completedDay(a: SalesActivity): string | null {
  if (a.status !== 'done' || !a.completedAt || !LOGGED_TYPES.has(a.type)) return null
  const t = new Date(a.completedAt)
  return Number.isNaN(t.getTime()) ? null : londonDay(t)
}

export type FlagTone = 'warn' | 'good' | 'accent'
export interface RepFlag { text: string; tone: FlagTone }
export interface StageSlice { stage: string; pence: number }

export interface RepRow {
  /** The employee id, or 'unassigned' for the open deals with no employee. */
  key: string
  name: string
  employeeId: string | null
  openDeals: SalesDeal[]
  openPence: number
  /** Σ the open deals' weightedPence; null when no deal carries one. */
  weightedPence: number | null
  /** Won inside the quarter (/forecast); null when the forecast is absent. */
  wonPence: number | null
  /** Completed this London week, credited by assignee. */
  week: number
  stages: StageSlice[]
  flag: RepFlag | null
}

export interface TeamInput {
  deals: SalesDeal[]
  forecast: SalesForecast | null
  /** The history of every status; null when it could not be read. */
  history: SalesActivity[] | null
  now: Date
}

const STAGE_ORDER = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation']

function sliceStages(deals: SalesDeal[]): StageSlice[] {
  const by = new Map<string, number>()
  for (const d of deals) by.set(d.stage, (by.get(d.stage) ?? 0) + (d.basePence ?? 0))
  const rank = (s: string) => { const i = STAGE_ORDER.indexOf(s); return i < 0 ? STAGE_ORDER.length : i }
  return [...by].map(([stage, pence]) => ({ stage, pence })).filter(s => s.pence > 0).sort((a, b) => rank(a.stage) - rank(b.stage) || a.stage.localeCompare(b.stage))
}

/** The rep flag, first match wins (spec §5.2 with the rulings): one deal over
 *  CONCENTRATION_PCT of the team's pipeline; the ONLY deal over R1's
 *  stage.over in the first stage (not dismissed); an open deal with no close
 *  date; won this London month; no open deals (no "first in line" — lead
 *  routing is not built). */
export function repFlag(row: Pick<RepRow, 'openDeals' | 'employeeId'>, teamPence: number, wonThisMonth: SalesDeal[], fmt: Fmt): RepFlag | null {
  const big = row.openDeals.map(d => ({ d, pct: sharePct(d.basePence, teamPence) })).filter(x => x.pct != null && x.pct > CONCENTRATION_PCT)
    .sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0))[0]
  if (big) return { text: `One deal is ${big.pct}% of the team's pipeline`, tone: 'warn' }
  if (row.openDeals.length === 1) {
    const d = row.openDeals[0]
    if (d.stage === FIRST_STAGE && d.assessment?.stage.over && !dismissedFor(d, ['stage_age'])) {
      const days = d.assessment.stage.days ?? 0
      return { text: `Only deal stuck ${days} ${plural(days, 'day', 'days')} in ${stageDisplay(d.stage)}`, tone: 'warn' }
    }
  }
  const undated = row.openDeals.find(d => !d.expectedClose)
  if (undated) return { text: `${undated.name} has no close date`, tone: 'warn' }
  if (row.employeeId && wonThisMonth.length > 0) {
    if (wonThisMonth.length === 1) {
      const w = wonThisMonth[0]
      return { text: `Won ${w.name} (${fmt(w.basePence ?? 0)}) this month`, tone: 'good' }
    }
    const total = wonThisMonth.reduce((s, d) => s + (d.basePence ?? 0), 0)
    return { text: `Won ${wonThisMonth.length} deals (${fmt(total)}) this month`, tone: 'good' }
  }
  if (row.employeeId && row.openDeals.length === 0) return { text: 'No open deals', tone: 'accent' }
  return null
}

export interface TeamModel {
  rows: RepRow[]
  /** Employees on the board (the Unassigned row is not a rep). */
  reps: number
  openCount: number
  openPence: number
  weightedPence: number | null
  /** Won in the quarter: Σ rows + unassigned; null without a forecast. */
  wonPence: number | null
  wonByRep: { name: string; pence: number }[]
  unassignedWonPence: number
  week: number
  lastWeek: number
  /** The newest completed call, email or meeting, when any exists. */
  lastLogged: { type: SalesActivity['type']; day: string } | null
  nothingLogged: { count: number; of: number; reps: number; unassigned: boolean; dismissed: number; assessed: number }
  /** History rows with no recorded author. */
  unauthored: number
  /** The largest open deal when it is over CONCENTRATION_PCT of the team's pipeline. */
  concentrated: { deal: SalesDeal; owner: string; pct: number } | null
  idleReps: string[]
}

export function teamModel({ deals, forecast, history, now }: TeamInput, fmt: Fmt): TeamModel {
  const today = londonDay(now)
  const monthKey = today.slice(0, 7)
  const mon = weekStart(today), lastMon = ago(mon, 7)
  const open = deals.filter(isOpenDeal)
  const openPence = open.reduce((s, d) => s + (d.basePence ?? 0), 0)

  // The roster: every forecast row (name, won), then any employee owning an
  // open deal the forecast did not name (its name from the deal's owner).
  const roster = new Map<string, { name: string; wonPence: number | null }>()
  for (const r of forecast?.rows ?? []) roster.set(r.employeeId, { name: r.employeeName || 'Unnamed employee', wonPence: r.wonPence })
  for (const d of open) {
    if (d.assignedEmployeeId && !roster.has(d.assignedEmployeeId)) roster.set(d.assignedEmployeeId, { name: d.owner && d.owner !== '—' ? d.owner : 'Unnamed employee', wonPence: forecast ? 0 : null })
  }

  const weekBy = new Map<string, number>()
  let week = 0, lastWeek = 0
  let lastLogged: TeamModel['lastLogged'] = null
  for (const a of history ?? []) {
    const day = completedDay(a)
    if (!day) continue
    if (!lastLogged || day > lastLogged.day) lastLogged = { type: a.type, day }
    if (day >= mon && day <= today) {
      week += 1
      if (a.assignedEmployeeId) weekBy.set(a.assignedEmployeeId, (weekBy.get(a.assignedEmployeeId) ?? 0) + 1)
    } else if (day >= lastMon && day < mon) lastWeek += 1
  }

  const wonMonth = (id: string) => deals.filter(d => d.stage === 'Closed Won' && d.assignedEmployeeId === id && d.closedAt && londonDay(new Date(d.closedAt)).slice(0, 7) === monthKey)
  const weighted = (ds: SalesDeal[]) => ds.some(d => d.weightedPence != null) ? ds.reduce((s, d) => s + (d.weightedPence ?? 0), 0) : null

  const rows: RepRow[] = []
  for (const [id, r] of roster) {
    const mine = open.filter(d => d.assignedEmployeeId === id)
    const base = { openDeals: mine, employeeId: id }
    rows.push({
      key: id, name: r.name, employeeId: id, openDeals: mine,
      openPence: mine.reduce((s, d) => s + (d.basePence ?? 0), 0),
      weightedPence: weighted(mine), wonPence: r.wonPence, week: weekBy.get(id) ?? 0,
      stages: sliceStages(mine), flag: repFlag(base, openPence, wonMonth(id), fmt),
    })
  }
  const orphans = open.filter(d => !d.assignedEmployeeId)
  if (orphans.length > 0) {
    rows.push({
      key: 'unassigned', name: 'Unassigned', employeeId: null, openDeals: orphans,
      openPence: orphans.reduce((s, d) => s + (d.basePence ?? 0), 0),
      weightedPence: weighted(orphans), wonPence: forecast ? forecast.unattributedWonPence : null, week: 0,
      stages: sliceStages(orphans), flag: repFlag({ openDeals: orphans, employeeId: null }, openPence, [], fmt),
    })
  }
  rows.sort((a, b) => b.openPence - a.openPence || a.name.localeCompare(b.name))

  // Deals with nothing logged: R1's recentActivity "never_logged", live; a
  // deal whose recent_activity family is dismissed is held back and counted.
  const assessed = open.filter(d => d.assessment)
  const never = assessed.filter(d => checkOf(d, 'recentActivity')?.reason === 'never_logged')
  const held = never.filter(d => dismissedFor(d, ['recent_activity']))
  const live = never.filter(d => !dismissedFor(d, ['recent_activity']))

  const top = [...open].sort((a, b) => (b.basePence ?? 0) - (a.basePence ?? 0))[0]
  const topPct = top ? sharePct(top.basePence, openPence) : null
  const ownerOf = (d: SalesDeal) => (d.assignedEmployeeId && roster.get(d.assignedEmployeeId)?.name) || 'Unassigned'

  return {
    rows,
    reps: roster.size,
    openCount: open.length,
    openPence,
    weightedPence: weighted(open),
    wonPence: forecast ? forecast.rows.reduce((s, r) => s + r.wonPence, 0) + forecast.unattributedWonPence : null,
    wonByRep: (forecast?.rows ?? []).filter(r => r.wonPence > 0).map(r => ({ name: r.employeeName || 'Unnamed employee', pence: r.wonPence })).sort((a, b) => b.pence - a.pence),
    unassignedWonPence: forecast?.unattributedWonPence ?? 0,
    week, lastWeek, lastLogged,
    nothingLogged: {
      count: live.length, of: open.length, assessed: assessed.length, dismissed: held.length,
      reps: new Set(live.filter(d => d.assignedEmployeeId).map(d => d.assignedEmployeeId)).size,
      unassigned: live.some(d => !d.assignedEmployeeId),
    },
    unauthored: (history ?? []).filter(a => !a.createdByUserId).length,
    concentrated: top && topPct != null && topPct > CONCENTRATION_PCT ? { deal: top, owner: ownerOf(top), pct: topPct } : null,
    idleReps: rows.filter(r => r.employeeId && r.openDeals.length === 0).map(r => r.name),
  }
}

/** First names for the copy: "Kate" from "Kate Shaw"; a one-word or empty
 *  name stays as it is. */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name

/** The Team lede (spec §2): "5 reps · £11.8M open · nothing logged by anyone
 *  this week." — the week clause from the history; absent history says so. */
export function teamLede(m: TeamModel, historyRead: boolean, fmt: Fmt): string {
  const head = `${m.reps} ${plural(m.reps, 'rep', 'reps')} · ${fmt(m.openPence)} open`
  if (!historyRead) return `${head}.`
  return m.week === 0 ? `${head} · nothing logged by anyone this week.` : `${head} · ${m.week} ${plural(m.week, 'activity', 'activities')} logged this week.`
}

/** "Kate £2.0M · Priya £1.0M · £240k unassigned" — three names at most. */
export function wonByRepLine(m: TeamModel, fmt: Fmt): string | null {
  const parts = m.wonByRep.slice(0, 3).map(r => `${firstName(r.name)} ${fmt(r.pence)}`)
  if (m.wonByRep.length > 3) parts.push(`${m.wonByRep.length - 3} more`)
  if (m.unassignedWonPence > 0) parts.push(`${fmt(m.unassignedWonPence)} unassigned`)
  return parts.length ? parts.join(' · ') : null
}

/** The fourth card's sub-line: whose deals, in words. */
export function nothingLoggedLine(n: TeamModel['nothingLogged']): string {
  if (n.count === 0) return n.of === 0 ? 'No open deals' : 'Every open deal has something logged'
  const who = n.reps > 0
    ? `${n.reps} ${plural(n.reps, 'rep', 'reps')}${n.unassigned ? ' and unassigned' : ''}`
    : 'unassigned deals'
  return `${n.count === n.of ? 'Every open deal' : `${n.count} open ${plural(n.count, 'deal', 'deals')}`}, across ${who}`
}

const TYPE_WORD: Record<string, string> = { call: 'a call', email: 'an email', meeting: 'a meeting' }
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dayWords = (day: string) => { const d = new Date(`${day}T12:00:00Z`); return `${DAY_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${MON_SHORT[d.getUTCMonth()]}` }

export interface TeamInsight { key: 'concentration' | 'activity' | 'attribution'; caption: string; title: string; body: string; action: { label: string; kind: 'deal' | 'activities'; dealId?: string } | null }

/** The right rail's insight cards (spec §5.3 with the rulings): concentration
 *  when one deal is over CONCENTRATION_PCT or a rep has no open deals;
 *  activity when nothing was logged this week; attribution when activities
 *  carry no author — the ruled sentence, the count templated, no "Require". */
export function teamInsights(m: TeamModel, historyRead: boolean, fmt: Fmt): TeamInsight[] {
  const out: TeamInsight[] = []
  if (m.concentrated || m.idleReps.length > 0) {
    const parts: string[] = []
    const c = m.concentrated
    if (c) parts.push(`${c.owner === 'Unassigned' ? 'An unassigned' : `${firstName(c.owner)}'s`} ${c.deal.name} deal is ${fmt(c.deal.basePence ?? 0)} of ${fmt(m.openPence)}.`)
    if (m.idleReps.length === 1) parts.push(`${firstName(m.idleReps[0])} has no open deals at all, so workload is uneven.`)
    else if (m.idleReps.length > 1) parts.push(`${m.idleReps.length} reps have no open deals at all, so workload is uneven.`)
    out.push({
      key: 'concentration', caption: '✦ Concentration',
      title: c ? 'The team forecast rests on one deal' : 'Workload is uneven',
      body: parts.join(' '),
      action: c ? { label: `Review ${c.deal.name} ›`, kind: 'deal', dealId: c.deal.id } : null,
    })
  }
  if (historyRead && m.week === 0) {
    const last = m.lastLogged ? `The last one completed was ${TYPE_WORD[m.lastLogged.type] ?? 'an activity'} on ${dayWords(m.lastLogged.day)}.` : 'No call, email or meeting has been completed yet.'
    out.push({ key: 'activity', caption: '✦ Activity', title: 'Nobody has logged anything this week', body: `No call, email or meeting was completed this week. ${last}`, action: { label: 'Open Activities ›', kind: 'activities' } })
  }
  if (historyRead && m.unauthored > 0) {
    const n = m.unauthored
    out.push({ key: 'attribution', caption: '✦ Attribution', title: `${n} ${plural(n, 'activity has', 'activities have')} no author`, body: `${n} ${plural(n, 'activity', 'activities')} ${plural(n, "doesn't", "don't")} record an author, so This week can't credit ${plural(n, 'it', 'them')} to anyone.`, action: { label: 'Open Activities ›', kind: 'activities' } })
  }
  return out
}

/** One open deal's amber note in Rep detail, R1 live, first match: stale in
 *  stage with nothing logged; no close date; close date passed. A note whose
 *  families are all dismissed falls through. */
export function dealNote(d: SalesDeal): string | null {
  const recent = checkOf(d, 'recentActivity'), close = checkOf(d, 'closeDate')
  if (d.assessment?.stage.over && recent?.reason === 'never_logged' && !dismissedFor(d, ['stage_age', 'recent_activity'])) {
    const days = d.assessment.stage.days ?? 0
    return `${days} ${plural(days, 'day', 'days')} in stage · nothing logged`
  }
  if (!d.expectedClose) return 'No close date'
  if (close?.reason === 'close_passed' && !dismissedFor(d, ['close_date'])) return 'Close date passed'
  return null
}

/** Rep detail's summary line (spec §5.2). */
export function repSummary(r: RepRow, quarter: string | null, fmt: Fmt): string {
  const parts = [`${fmt(r.openPence)} open`]
  if (r.weightedPence != null) parts.push(`${fmt(r.weightedPence)} weighted`)
  if (r.wonPence != null) parts.push(`${fmt(r.wonPence)} won in ${quarter ?? 'the quarter'}`)
  if (r.employeeId) parts.push(`${r.week} ${plural(r.week, 'activity', 'activities')} this week`)
  return parts.join(' · ')
}
